"use client";
import type { ReportQuery, ReportQueryResult } from "@gettactic/api";
import { useAuthenticated, useOrgFeatureFlag } from "@gettactic/hooks";
import React from "react";
import { useReportQuery } from "@/features/reporting/hooks/useReportQuery";
import { canAccessAnalytics } from "@/features/reporting/lib/access";

const DESKS = [{ dimension: "resource_type", in: ["desk", "workspace"] }];

/** Whether this user may read the analytics facts the zone pages decorate themselves with. */
export const useCanSeeZoneUsage = (): boolean => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const hasReporting = useOrgFeatureFlag("reporting");
  return hasReporting === true && canAccessAnalytics(authenticatedUser);
};

export interface UsageByMember {
  value: number;
  previous: number | null;
}

/**
 * Desk utilization over the last 30 days per member of one place dimension
 * (zone or space), from the analytics facts. Empty when the user cannot see
 * analytics or the facts have not been computed.
 */
export const useUtilizationBy = (
  dimension: "zone" | "space",
  filters: ReportQuery["filters"] = []
) => {
  const canSee = useCanSeeZoneUsage();
  const query = React.useMemo<ReportQuery | null>(
    () =>
      canSee
        ? {
            measure: "occupancy",
            breakdown: [dimension],
            filters: [...DESKS, ...filters],
            period: { preset: "last_30_days" },
            compare: "previous_period"
          }
        : null,
    [canSee, dimension, filters]
  );
  const { data, isLoading } = useReportQuery(query);

  const byMember = React.useMemo(() => {
    const map = new Map<string, UsageByMember>();
    for (const row of data?.rows ?? []) {
      map.set(row.keys[0], { value: row.value, previous: row.previous });
    }
    return map;
  }, [data]);

  return {
    byMember,
    isLoading: canSee && isLoading,
    canSee,
    measure: data?.meta.measure
  };
};

/** One measure for one zone, last 30 days against the 30 before. */
export const useZoneMeasure = (
  zoneId: string,
  measure: string,
  enabled = true
): { result: ReportQueryResult | undefined; isLoading: boolean } => {
  const canSee = useCanSeeZoneUsage();
  const query = React.useMemo<ReportQuery | null>(
    () =>
      canSee && enabled
        ? {
            measure,
            filters: [...DESKS, { dimension: "zone", in: [zoneId] }],
            period: { preset: "last_30_days" },
            compare: "previous_period"
          }
        : null,
    [canSee, enabled, measure, zoneId]
  );
  const { data, isLoading } = useReportQuery(query);
  return { result: data, isLoading: canSee && isLoading };
};

/** Reservations per week over the last 90 days for one zone, for a trend chart. */
export const useZoneTrend = (zoneId: string) => {
  const canSee = useCanSeeZoneUsage();
  const query = React.useMemo<ReportQuery | null>(
    () =>
      canSee
        ? {
            measure: "reservations",
            filters: [...DESKS, { dimension: "zone", in: [zoneId] }],
            period: { preset: "last_90_days", grain: "week" },
            compare: "previous_period"
          }
        : null,
    [canSee, zoneId]
  );
  const { data } = useReportQuery(query);
  return { query, result: data };
};
